import { IconUserDuo } from "@central-icons-react/round-filled-radius-2-stroke-2/IconUserDuo";
import { IconCalendar2 } from "@central-icons-react/round-filled-radius-2-stroke-2/IconCalendar2";
import { IconClock } from "@central-icons-react/round-filled-radius-2-stroke-2/IconClock";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { cancelSession } from "../apiClient";
import { useOrderStore } from "../stores/orderStore";
import { useSessionStore } from "../stores/sessionStore";
import { formatDay, formatPrice } from "../utils/format";
import {
  sessionToGtmItems,
  trackRemoveFromCart,
  trackViewCart,
} from "../utils/gtm";
import { Message } from "./ui/Message";
import { AlertDialog } from "./ui/AlertDialog";

export default function OrderSummary({
  hideTitle = false,
  trackView = true,
}: {
  hideTitle?: boolean;
  trackView?: boolean;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const orderStore = useOrderStore();
  const { session, clearSession } = useSessionStore();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const lastTrackedSessionId = useRef<string | null>(null);

  useEffect(() => {
    if (!trackView || !session) return;
    // Desktop summary lives inside a `hidden sm:block` wrapper — skip firing
    // when actually invisible on mobile (mobile dialog fires its own view_cart).
    if (typeof window !== "undefined" && !window.matchMedia("(min-width: 640px)").matches) {
      return;
    }
    if (lastTrackedSessionId.current === session.id) return;
    lastTrackedSessionId.current = session.id;
    trackViewCart(session);
  }, [trackView, session]);

  const sessionItem = session?.items[0];
  const sessionProducts = session?.products ?? [];
  const slotTime = sessionItem?.slotTime?.substring(0, 5) ?? null;

  const isCalendar = location.pathname === "/";

  const cancelMutation = useMutation({
    mutationFn: () => cancelSession(session!.id),
    onSettled: () => {
      clearSession();
      setConfirmOpen(false);
      navigate("/");
    },
  });

  const handleWijzigen = () => setConfirmOpen(true);

  const handleConfirm = () => {
    if (session) {
      trackRemoveFromCart(
        sessionToGtmItems(session),
        session.totalAfterDiscount ?? session.total,
      );
      cancelMutation.mutate();
    } else {
      clearSession();
      setConfirmOpen(false);
      navigate("/");
    }
  };

  return (
    <>
      <div className="bg-white border border-border p-6 rounded-xl">
        {(!hideTitle || !isCalendar) && (
          <div className="flex justify-between items-baseline mb-5">
            {!hideTitle && (
              <p className="font-semibold text-xl">{t("orderSummary.title")}</p>
            )}
            {!isCalendar && (
              <button
                className="text-sm text-blue underline transition-colors"
                onClick={handleWijzigen}
              >
                {t("orderSummary.edit")}
              </button>
            )}
          </div>
        )}
        <ul className="flex flex-col gap-1.5">
          <li className="flex items-center gap-3">
            <IconUserDuo size={22} />
            {orderStore.personCount > 0 ? (
              <span>
                {orderStore.personCount}{" "}
                {orderStore.personCount === 1 ? t("common.person_one") : t("common.person_other")}
              </span>
            ) : (
              <span className="text-black/40">{t("orderSummary.makeChoice")}</span>
            )}
          </li>
          <li className="flex items-center gap-3">
            <IconCalendar2 size={22} />
            {orderStore.selectedDate ? (
              <span>{formatDay(orderStore.selectedDate)}</span>
            ) : (
              <span className="text-black/40">{t("orderSummary.makeChoice")}</span>
            )}
          </li>
          {sessionItem && (
            <li className="flex items-center gap-3">
              <IconClock size={22} />
              <span>{slotTime}</span>
            </li>
          )}
        </ul>

        {sessionItem && (
          <div className="mt-5 flex gap-1 justify-between">
            <span>
              {sessionItem.facilitiesNeeded > 1 &&
                `${sessionItem.facilitiesNeeded}× `}
              {sessionItem.semItemConfig.name}
            </span>
            <span className="text-black/60">
              {formatPrice(sessionItem.pricePerUnitIn)}
            </span>
          </div>
        )}

        {sessionProducts.length > 0 && (
          <div className="mt-5">
            <p className="text-black/40 mb-3">{t("orderSummary.extras")}</p>
            <ul className="flex flex-col gap-3">
              {sessionProducts.map((p) => {
                const multiplier =
                  p.semProductConfig.type === "perPersonTotal"
                    ? p.quantity * (session?.personCount ?? 1)
                    : p.quantity;
                return (
                  <li key={p.id} className="flex gap-1 justify-between">
                    <span>
                      {multiplier > 1 && (
                        <span className="text-black/40 mr-2">
                          {multiplier}×
                        </span>
                      )}
                      {p.semProductConfig.name}
                    </span>
                    <span className="text-black/60">
                      {formatPrice(p.pricePerUnitIn * multiplier)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {session && (
          <div className="mt-5 -mx-6 -mb-6 px-6 py-4 bg-background-secondary rounded-b-xl">
            {session.discount && (
              <div className="flex justify-between text-sm text-green-600 mb-1">
                <span>{t("orderSummary.discountCode")}</span>
                <span>−{formatPrice(session.discount.amount)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold text-base">
              <span>{t("orderSummary.total")}</span>
              <span>{formatPrice(session.totalAfterDiscount)}</span>
            </div>
          </div>
        )}

        {!orderStore.selectedDate && (
          <Message iconName="IconCircleInfo" className="mt-4">
            {t("calendar.chooseDate")}
          </Message>
        )}
      </div>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("orderSummary.confirmTitle")}
        description={
          session
            ? t("orderSummary.confirmWithSession")
            : t("orderSummary.confirmWithoutSession")
        }
      >
        <div className="flex gap-3 justify-end">
          <button
            className="btn-outline"
            onClick={() => setConfirmOpen(false)}
            disabled={cancelMutation.isPending}
          >
            {t("common.cancel")}
          </button>
          <button
            className="btn-blue"
            onClick={handleConfirm}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? t("common.loading") : t("orderSummary.confirmButton")}
          </button>
        </div>
      </AlertDialog>
    </>
  );
}